import { useMemo, useState } from "react"

import { Flex } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import Table from "../../../../Components/Table"
import { formatCurrency } from "../../../../Utils/utils"
import { referralColorMap, referralTextMap } from "../../utils/constants"
import { columns } from "./columns"
import AlertsModal from "./components/AlertsModal"
import Empty from "./components/Empty"

import "./styles.less"

const maskEmail = (email) => {
  if (!email) return email
  const [local, domain] = email.split("@")
  if (!domain) return email
  const dotIdx = domain.indexOf(".")
  const domainName = dotIdx !== -1 ? domain.slice(0, dotIdx) : domain
  const tld = dotIdx !== -1 ? domain.slice(dotIdx) : ""
  return `${local[0]}****@${domainName[0]}***${tld}`
}

const maskName = (name) => {
  if (!name) return name
  if (name.length <= 2) return name
  const prefixLen = Math.min(3, name.length - 1)
  return `${name.slice(0, prefixLen)}******${name.slice(-1)}`
}

const ReferralsTable = () => {
  const { t } = useTranslation()
  const [showList, setShowList] = useState(false)
  const [selectedBaId, setSelectedBaId] = useState("")
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const { theme, isMobile } = useSelector((state) => state.nav)
  const { isAdmin } = useSelector((state) => state.auth)
  const { referralsTable, referralsTableFetching } = useSelector((state) => state.referrals)

  const flattenedData = useMemo(
    () =>
      (showList ? referralsTable : referralsTable?.slice(0, 2))?.flatMap((referral) =>
        referral?.business_accounts?.length === 0
          ? {
              user_email: referral.user_email,
              business_account: null,
              created: null,
              status: null,
              reward_amount: null,
              alerts: null,
              id: null,
              userRowSpan: 1
            }
          : referral.business_accounts.map((account, i) => ({
              user_email: referral.user_email,
              business_account: account.name,
              created: account.created,
              status: account.status,
              reward_amount: account.reward_amount,
              alerts: account.alerts,
              id: account.business_account_id,
              userRowSpan: i === 0 ? referral.business_accounts.length : 0
            }))
      ),
    [showList, referralsTable]
  )

  const groupedData = useMemo(() => {
    if (!flattenedData) return []
    const groups = []
    const seen = new Map()
    for (const row of flattenedData) {
      if (!seen.has(row.user_email)) {
        const group = { email: row.user_email, accounts: [] }
        seen.set(row.user_email, group)
        groups.push(group)
      }
      if (row.business_account !== null || row.id !== null) {
        seen.get(row.user_email).accounts.push(row)
      }
    }
    return groups
  }, [flattenedData])

  const handleClickAlerts = (id) => {
    setModalIsOpen(true)
    setSelectedBaId(id)
  }

  const closeModal = () => setModalIsOpen(false)

  if (isMobile) {
    return (
      <div className="referrals-table referrals-table--mobile">
        {groupedData.map((group) => (
          <div key={group.email} className="referrals-table__group">
            <span className="referrals-table__group-email">{maskEmail(group.email)}</span>
            <div className="referrals-table__cards-row">
              {group.accounts.length === 0 ? (
                <div className="referrals-table__card">
                  <span className="referrals-table__field-label">{t("No business accounts")}</span>
                </div>
              ) : (
                group.accounts.map((account, i) => {
                  const statusColor = referralColorMap[account.status]
                  const dateFormatted = account.created
                    ? dayjs(account.created).format("DD/MM/YYYY")
                    : "—"

                  return (
                    <div key={i} className="referrals-table__card">
                      <div className="referrals-table__card-row">
                        <div className="referrals-table__field">
                          <span className="referrals-table__field-label">{t("Date")}</span>
                          <span className="referrals-table__field-value">{dateFormatted}</span>
                        </div>
                        <div className="referrals-table__field">
                          <span className="referrals-table__field-label">
                            {t("Business account")}
                          </span>
                          <span className="referrals-table__field-value referrals-table__field-value--truncate">
                            {account.business_account ? maskName(account.business_account) : "—"}
                          </span>
                        </div>
                      </div>
                      <div className="referrals-table__card-row">
                        <div className="referrals-table__field">
                          <span className="referrals-table__field-label">{t("Status")}</span>
                          {account.status ? (
                            <Flex align="center" gap={4}>
                              <span
                                className="referrals-table__field-value"
                                style={{ color: statusColor }}
                              >
                                {referralTextMap[account.status] || account.status}
                              </span>
                            </Flex>
                          ) : (
                            <span className="referrals-table__field-value">—</span>
                          )}
                        </div>
                        <div className="referrals-table__field">
                          <span className="referrals-table__field-label">{t("Alerts")}</span>
                          <button
                            className="referrals-table__alerts-btn"
                            onClick={() => account.id && handleClickAlerts(account.id)}
                            disabled={!account.business_account}
                          >
                            {account.alerts ?? "0"}
                          </button>
                        </div>
                      </div>
                      <div className="referrals-table__field">
                        <span className="referrals-table__field-label">{t("Total reward")}</span>
                        <span className="referrals-table__field-value">
                          {account.reward_amount != null
                            ? formatCurrency(account.reward_amount / 100)
                            : "—"}
                        </span>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        ))}
        <div className="view-all-row">
          {referralsTable?.length > 2 && !showList ? (
            <ButtonComponent onClick={() => setShowList(true)}>
              {t("View all the list")}
            </ButtonComponent>
          ) : null}
        </div>
        <AlertsModal isOpen={modalIsOpen} baId={selectedBaId} closeModal={closeModal} />
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 32 }} className="referrals-table">
      <Table
        columns={columns({
          t,
          data: flattenedData || [],
          handleClickAlerts,
          theme
        })}
        loading={referralsTableFetching}
        tableLayout="fixed"
        dataSource={flattenedData}
        expandable={{ defaultExpandAllRows: true }}
        EmptyComponent={isAdmin ? null : Empty}
        withOutPagination={true}
        scroll={{}}
      />
      <Flex justify="center" className="view-all-row">
        {referralsTable?.length > 2 && !showList ? (
          <ButtonComponent onClick={() => setShowList(true)}>View all the list</ButtonComponent>
        ) : null}
      </Flex>
      <AlertsModal isOpen={modalIsOpen} baId={selectedBaId} closeModal={closeModal} />
    </div>
  )
}

export default ReferralsTable
